/** Parse Google Places weekday_text and estimate open/closed status. */

export type OpeningHoursStatus = {
  isOpen: boolean | null;
  statusLabel: string;
  /** Hours text shown next to status (may be yesterday when still open overnight). */
  summaryHours: string;
  /** Full weekday line to bold in the weekly list (calendar today). */
  todayLine: string;
  lines: string[];
};

const CLOSED_RE =
  /^(closed|休息|休業|定休日|휴무|ปิด|geschlossen|ferm[eé]|cerrado)/i;

const DAY_ALIASES: Record<string, number> = {
  sunday: 0,
  sun: 0,
  monday: 1,
  mon: 1,
  tuesday: 2,
  tue: 2,
  tues: 2,
  wednesday: 3,
  wed: 3,
  thursday: 4,
  thu: 4,
  thur: 4,
  thurs: 4,
  friday: 5,
  fri: 5,
  saturday: 6,
  sat: 6,
  日曜日: 0,
  月曜日: 1,
  火曜日: 2,
  水曜日: 3,
  木曜日: 4,
  金曜日: 5,
  土曜日: 6,
  星期日: 0,
  星期天: 0,
  星期一: 1,
  星期二: 2,
  星期三: 3,
  星期四: 4,
  星期五: 5,
  星期六: 6,
  週日: 0,
  周日: 0,
  週一: 1,
  周一: 1,
  週二: 2,
  周二: 2,
  週三: 3,
  周三: 3,
  週四: 4,
  周四: 4,
  週五: 5,
  周五: 5,
  週六: 6,
  周六: 6,
};

const normalizeSpaces = (value: string) =>
  value.replace(/[\u202f\u00a0\u2009]/g, " ").replace(/\s+/g, " ").trim();

const splitDayAndHours = (
  line: string,
): { day: string; hours: string } | null => {
  const normalized = normalizeSpaces(line);
  const idx = normalized.indexOf(":");
  if (idx <= 0) return null;
  return {
    day: normalized.slice(0, idx).trim(),
    hours: normalized.slice(idx + 1).trim(),
  };
};

const dayIndexFromLabel = (day: string): number | null => {
  const key = day.trim().toLowerCase();
  if (key in DAY_ALIASES) return DAY_ALIASES[key];
  // "月曜日" style without exact map already covered; try startsWith English
  for (const [alias, index] of Object.entries(DAY_ALIASES)) {
    if (key.startsWith(alias)) return index;
  }
  return null;
};

const parseClockToMinutes = (
  raw: string,
  inheritedMeridian?: "AM" | "PM",
): { minutes: number; meridian?: "AM" | "PM" } | null => {
  const text = normalizeSpaces(raw);
  const match = text.match(/^(\d{1,2}):(\d{2})\s*([AaPp][Mm])?$/);
  if (!match) return null;

  let hour = Number(match[1]);
  const minute = Number(match[2]);
  if (Number.isNaN(hour) || Number.isNaN(minute) || minute > 59) return null;

  const meridian = (match[3]?.toUpperCase() as "AM" | "PM" | undefined) ||
    inheritedMeridian;

  if (meridian) {
    if (hour === 12) hour = 0;
    if (meridian === "PM") hour += 12;
  } else if (hour > 23) {
    return null;
  }

  return { minutes: hour * 60 + minute, meridian };
};

const parseTimeRange = (
  rangeText: string,
): { start: number; end: number } | null => {
  const text = normalizeSpaces(rangeText);
  const parts = text.split(/\s*[–—−-]\s*/);
  if (parts.length !== 2) return null;

  const endParsed = parseClockToMinutes(parts[1]);
  if (!endParsed) return null;

  // "1:00 – 9:00 PM" => start inherits PM from end when missing meridian
  const startParsed = parseClockToMinutes(parts[0], endParsed.meridian);
  if (!startParsed) return null;

  let { minutes: start } = startParsed;
  let { minutes: end } = endParsed;

  // Overnight e.g. 3:00 PM – 1:00 AM
  if (end <= start) end += 24 * 60;

  return { start, end };
};

const parseHoursSegments = (
  hoursText: string,
): Array<{ start: number; end: number }> | "closed" | "unknown" => {
  const text = normalizeSpaces(hoursText);
  if (!text) return "unknown";
  if (CLOSED_RE.test(text)) return "closed";
  if (/^(open\s*24\s*hours|24\s*hours|终日|24時間)/i.test(text)) {
    return [{ start: 0, end: 24 * 60 }];
  }

  const ranges = text
    .split(/\s*,\s*/)
    .map((part) => parseTimeRange(part))
    .filter((range): range is { start: number; end: number } => range != null);

  if (!ranges.length) return "unknown";
  return ranges;
};

const isOpenAt = (
  segments: Array<{ start: number; end: number }>,
  minutesNow: number,
): boolean => {
  // Also check previous-day overnight window via +24h on now for ranges that crossed midnight
  // ranges already extend end past midnight; also check minutesNow + 24*60 for late-night after midnight
  return segments.some(
    (range) =>
      (minutesNow >= range.start && minutesNow < range.end) ||
      (minutesNow + 24 * 60 >= range.start && minutesNow + 24 * 60 < range.end),
  );
};

export function getOpeningHoursStatus(
  openingHours: string[] | undefined,
  now: Date = new Date(),
): OpeningHoursStatus | null {
  if (!openingHours?.length) return null;

  const lines = openingHours.map((line) => normalizeSpaces(line));
  const todayIndex = now.getDay();
  const minutesNow = now.getHours() * 60 + now.getMinutes();

  let todayLine = lines.find((line) => {
    const parsed = splitDayAndHours(line);
    if (!parsed) return false;
    return dayIndexFromLabel(parsed.day) === todayIndex;
  });

  // weekday_text is usually Mon→Sun; fall back by index if labels are unfamiliar
  if (!todayLine) {
    // Google order is often Monday-first
    const mondayFirstIndex = (todayIndex + 6) % 7;
    todayLine = lines[mondayFirstIndex] || lines[todayIndex] || lines[0];
  }

  const todayParsed = todayLine ? splitDayAndHours(todayLine) : null;
  const todayHours = todayParsed?.hours || "";
  const segments = todayHours ? parseHoursSegments(todayHours) : "unknown";

  let isOpen: boolean | null = null;
  if (segments === "closed") isOpen = false;
  else if (segments === "unknown") isOpen = null;
  else isOpen = isOpenAt(segments, minutesNow);

  let displayLine = todayLine || "";

  // Overnight: if today says closed/unknown, check yesterday's overnight range
  if (isOpen === false || isOpen === null) {
    const yesterdayIndex = (todayIndex + 6) % 7;
    const yesterdayLine = lines.find((line) => {
      const parsed = splitDayAndHours(line);
      return parsed ? dayIndexFromLabel(parsed.day) === yesterdayIndex : false;
    });
    if (yesterdayLine) {
      const yHours = splitDayAndHours(yesterdayLine)?.hours;
      if (yHours) {
        const ySegments = parseHoursSegments(yHours);
        if (Array.isArray(ySegments)) {
          const overnightOpen = ySegments.some(
            (range) =>
              range.end > 24 * 60 &&
              minutesNow + 24 * 60 >= range.start &&
              minutesNow + 24 * 60 < range.end,
          );
          if (overnightOpen) {
            isOpen = true;
            displayLine = yesterdayLine;
          }
        }
      }
    }
  }

  const statusLabel =
    isOpen === true ? "營業中" : isOpen === false ? "休息中" : "營業時間";

  const summarySource = displayLine || todayLine || "";
  const summaryHours = summarySource.includes(":")
    ? summarySource.slice(summarySource.indexOf(":") + 1).trim()
    : summarySource;

  return {
    isOpen,
    statusLabel,
    summaryHours,
    todayLine: todayLine || "",
    lines,
  };
}
